import { ErrorHandler, inject, Injectable } from '@angular/core';
import * as Sentry from "@sentry/angular";
import { SpinnerService } from '../../core/service/spinner.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
  private spinner = inject(SpinnerService);

  private readonly chunkFailedMessageV1 = /Loading chunk [\d]+ failed/;
  private readonly chunkFailedMessageV2 = 'TypeError: Failed to fetch dynamically imported module';
  private readonly isAlreadyRefreshedString = 'isAlreadyRefreshed';

  handleError(error: any): void {
    console.error(error);
    const parsedError = this.formatError(error);
    const isErrorIgnored = this.shouldErrorBeIgnored(parsedError);
    if (isErrorIgnored) {
      return;
    }
    this.spinner.resetActiveRequests();

    // Check for chunk errors and try to refresh the browser if needed
    if (this.isChunkMessageProvided(parsedError)) {
      this.tryToRefreshBrowser();
    }

    // Forward the error to Sentry for tracking
    Sentry.captureException(error);
  }

  private isChunkMessageProvided(error: string): boolean {
    const isV1Satisfied = this.chunkFailedMessageV1.test(error);
    const isV2Satisfied = error.includes(this.chunkFailedMessageV2);
    return isV1Satisfied || isV2Satisfied;
  }

  /**
   * Checks if the browser was recently refreshed.
   * If the browser was not refreshed in this session, refreshes the browser and marks as refreshed.
   * We need it so that in case the error occurs multiple times, we do not refresh the window in a loop.
   */
  private tryToRefreshBrowser(): void {
    const isAlreadyRefreshed = this.getIsAlreadyRefreshed();
    if (isAlreadyRefreshed) {
      Sentry.captureException(new Error('Chunks error, already refreshed :O'));
      return;
    }
    sessionStorage.setItem(this.isAlreadyRefreshedString, `${new Date().getTime()}`);
    window.location.reload();
  }

  /**
   * Checks if the browser was recently refreshed.
   * If the browser was refreshed more than 1 minute ago, returns false,
   * because it means the localstorage stores info about too late refresh.
   * Otherwise, returns true.
   */
  private getIsAlreadyRefreshed(): boolean {
    const fromSessionStorage = sessionStorage.getItem(this.isAlreadyRefreshedString);
    if (!fromSessionStorage) {
      return false;
    }
    const parsedEpoch = Number(fromSessionStorage);
    if (isNaN(parsedEpoch)) {
      return false;
    }
    const currentTime = (new Date()).getTime();
    const diff = currentTime - parsedEpoch;
    return diff < 60;
  }

  private shouldErrorBeIgnored(error: string): boolean {
    // do not care about NG0100 "ExpressionChangedAfterItHasBeenCheckedError"
    if (error.includes('NG0100')) {
      return true;
    }
    return false;
  }

  /**
   * Parses error from a usual object type to string.
   * A simple .toString() provides only the message: without the stack trace or code.
   * JSON.stringify also did not help :(
   *
   * Usually an error has the following structure:
   * error: {
   *   message: string;
   *   name: string;
   *   stacktrace: string[];
   * }
   *
   * @param error is generic of any type.
   */
  private formatError(error: any): string {
    if (!error) {
      return 'Unknown error';
    }

    // Handle native Error and objects with similar structure
    if (error instanceof Error || (error.name && error.message)) {
      // Compose a string with all relevant fields
      let errorString = `${error.name || 'Error'}: ${error.message || ''}`;
      if (error.stack) {
        errorString += `\nStack:\n${error.stack}`;
      }
      // Optionally, add other enumerable properties
      for (const key in error) {
        if (!['name', 'message', 'stack'].includes(key)) {
          errorString += `\n${key}: ${JSON.stringify(error[key])}`;
        }
      }
      return errorString;
    }

    // Handle non-standard error structures
    try {
      return JSON.stringify(error, Object.getOwnPropertyNames(error));
    } catch {
      return String(error);
    }
  }
}
